import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import { UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { MatTooltip } from '@angular/material/tooltip';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';

// Services
import { RefreshService } from 'src/app/refresh.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ApiService } from 'src/app/services/api.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';

// Constants
import {
  ACCOUNTS_TABLE_SUB_HEADERS,
  ACCOUNTS_DEFAULT_PAGE_SIZE,
  ACCOUNTS_DEFAULT_SORT_COLUMN,
  ACCOUNTS_DEFAULT_SORT_TYPE,
  ACCOUNT_TYPE_OPTIONS,
  ACCOUNT_STATUS_OPTIONS,
  ACCOUNT_TIER_TYPE_OPTIONS,
  EMAIL_VERIFIED_OPTIONS,
} from 'src/app/lib/constants/accounts.constants';

// Modules
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { SharedPipeModule } from 'src/app/shared/modules/shared-pipe.module';
import { PaginationComponent } from 'src/app/shared/component/pagination/pagination.component';
import { TableControlsComponent } from 'src/app/shared/component/table-controls/table-controls.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { DateInputComponent } from 'src/app/shared/component/date-input/date-input.component';
import { DropdownFieldV2Component } from 'src/app/shared/component/custom-dropdown-v2/custom-dropdown-v2.component'
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
  selector: 'app-accounts',
  templateUrl: './accounts.component.html',
  styleUrl: './accounts.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedComponentsModule,
    NgxSpinnerModule,
    MegapayLoaderComponent,
    SharedPipeModule,
    PaginationComponent,
    TableControlsComponent,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    DateInputComponent,
    SharedDirectivesModule,
    DropdownFieldV2Component,
    MatTooltip,
  ],
})
export class AccountsComponent implements OnInit, OnDestroy {
  @ViewChildren(DropdownFieldV2Component) dropdowns!: QueryList<DropdownFieldV2Component>;
  private destroy$ = new Subject<void>();
  private search$  = new Subject<string>();


  // ─── Tabs & table config ──────────────────────────────────────────────────
  tableSubHeader = ACCOUNTS_TABLE_SUB_HEADERS;

  // ─── Forms ────────────────────────────────────────────────────────────────
  form: any;
  limitForm: any;

  // ─── Dropdown options ─────────────────────────────────────────────────────
  readonly accountTypeOptions   = ACCOUNT_TYPE_OPTIONS;
  readonly statusOptions        = ACCOUNT_STATUS_OPTIONS;
  readonly tierTypeOptions      = ACCOUNT_TIER_TYPE_OPTIONS;
  readonly emailVerifiedOptions = EMAIL_VERIFIED_OPTIONS;

  // Selected dropdown display values
  accountType:      any = null;
  accountType_text: any = null;
  statusType:       any = null;
  statusType_text:  any = null;
  tierType:         any = null;
  tierType_text:    any = null;
  selectedVerificationOption: any = null;

  // ─── State ────────────────────────────────────────────────────────────────
  showTable    = false;
  submitted    = false;
  isLoading    = false;

  // ─── Data ─────────────────────────────────────────────────────────────────
  account_lists: any[] = [];

  // ─── Filter ───────────────────────────────────────────────────────────────
  filterFields: any;

  // ─── Pagination ───────────────────────────────────────────────────────────
  size         = ACCOUNTS_DEFAULT_PAGE_SIZE;
  total_pages  = 0;
  current_page = 1;
  pages_array: number[] = [];
  total        = 0;

  // ─── Search ───────────────────────────────────────────────────────────────
  search_keyword = '';

  // ─── Sorting ──────────────────────────────────────────────────────────────
  sort_column = ACCOUNTS_DEFAULT_SORT_COLUMN;
  sort_type: 'ASC' | 'DESC' = ACCOUNTS_DEFAULT_SORT_TYPE;

  // ─── Toggle guard (prevents double-clicks) ────────────────────────────────
  togglingUid: any = null;

  constructor(
    private fb:               UntypedFormBuilder,
    private spinner:          NgxSpinnerService,
    private toastr:           ToastrService,
    private router:           Router,
    public  apiService:       ApiService,
    public  paginationHelper: PaginationHelperService,
    private refreshService:   RefreshService,
    private popUpDialog:      PopUpDialogService,
    private authService:      AuthService,
  ) {
    this.initializeForm();
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.spinner.hide().catch(console.error);

    // Always show table on load
    this.filterFields = {}; 
    this.get_filtered_list();

    // Debounced search stream
    this.search$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(keyword => {
        this.search_keyword = keyword;
        this.current_page   = 1;
        this.get_filtered_list();
      });

    // Global table-reset signal
    this.refreshService.resetTable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetTableState());
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch(console.error);
  }

  // ─── Form initialisation ──────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      firstName:        [null],
      lastName:         [null],
      phoneNumber:      [null],
      referrerNumber:   [null],
      email:            [null],
      dateFrom:         [null],
      dateTo:           [null],
      accountType:      [null],
      status:           [null],
      tierType:         [null],
      isEmailVerified:  [null],
    });

    this.limitForm = this.fb.group({ limit: [this.size] });
  }

  // ─── Reset helpers ────────────────────────────────────────────────────────
  private clearFilters(): void {
    this.form.reset();

    this.accountType       = null;  this.accountType_text  = '';
    this.statusType        = null;  this.statusType_text   = '';
    this.tierType          = null;  this.tierType_text     = '';
    this.selectedVerificationOption = null;

    this.dropdowns?.forEach(d => d.reset());
  }

  public resetTableState(): void {
    this.clearFilters(); 
    this.limitForm.reset({ limit: ACCOUNTS_DEFAULT_PAGE_SIZE });

    this.account_lists   = [];
    this.isLoading       = false;

    this.size            = ACCOUNTS_DEFAULT_PAGE_SIZE;
    this.total_pages     = 0;
    this.current_page    = 1;
    this.pages_array     = [];
    this.total           = 0;
    this.search_keyword  = '';
    this.sort_column     = ACCOUNTS_DEFAULT_SORT_COLUMN;
    this.sort_type       = ACCOUNTS_DEFAULT_SORT_TYPE;
    this.filterFields    = {};
    this.get_filtered_list();
  }

  // ─── Dropdown setters ─────────────────────────────────────────────────────

  setAccountType(label: string, value: string): void {
    this.accountType      = value;
    this.accountType_text = label;
  }

  setStatusType(label: string, value: string): void {
    this.statusType      = value;
    this.statusType_text = label;
  }

  setTierType(label: string, value: string): void {
    this.tierType      = value;
    this.tierType_text = label;
  }

  setIsVerifiedType(event: { label: string; value: string }): void {
    this.selectedVerificationOption = event;
    this.form.patchValue({ isEmailVerified: event.value });
  }

  // ─── Date validation ──────────────────────────────────────────────────────

  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom').value;
    const dateTo   = this.form.get('dateTo').value;

    if (dateFrom && dateTo) {
      const from = new Date(dateFrom);
      const to   = new Date(dateTo);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      if (to < from) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  // ─── Submit (initial filter) ──────────────────────────────────────────────

  submit(): void {
    if (!this.validateDates()) { return; }

    this.isLoading    = true;
    this.current_page = 1;
    this.spinner.show().catch(console.error);

    const params = {
      firstName:      this.form.controls.firstName.value,
      lastName:       this.form.controls.lastName.value,
      phoneNumber:    this.form.controls.phoneNumber.value,
      referrerNumber: this.form.controls.referrerNumber.value,
      email:          this.form.controls.email.value,
      dateFrom:       this.form.controls.dateFrom.value,
      dateTo:         this.form.controls.dateTo.value,
      accountType:    this.accountType,
      status:         this.statusType,
      tierType:       this.tierType,
      isEmailVerified: this.selectedVerificationOption?.value ?? null,
      sortBy:         { [this.sort_column]: this.sort_type },
    };

    this.filterFields = params;
    this.pages_array  = [];

    this.apiService.getFilteredAccounts(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.account_lists = response.result.data;
            this.submitted     = true;
            this.showTable     = true;

            this.pages_array  = this.paginationHelper.generatePageArray(
              this.current_page, response.result.totalPages
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);

            this.clearFilters();
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Accounts'
          );
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Filtered list (search / sort / page change) ──────────────────────────

  get_filtered_list(): void {
    this.isLoading = true;
    this.spinner.show().catch(console.error); // ← spinner owned here

    this.filterFields = {
      ...this.filterFields,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    const params = {
      ...this.filterFields,
      search: this.search_keyword,
    };

    this.apiService.getFilteredAccounts(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.account_lists = response.result.data;
            this.pages_array   = this.paginationHelper.generatePageArray(
              this.current_page, response.result.totalPages
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error('Something went wrong. Please check you connection and try again.');
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error); 
        },
      });
  }

  // ─── Table controls ───────────────────────────────────────────────────────

  onSearchKeyupFromComponent(event: any): void {
    const value = (event.target as HTMLInputElement).value;
    this.search$.next(value);
  }

  onLimitChangeFromComponent(event: any): void {
    this.size         = Number((event.target as HTMLInputElement).value);
    this.current_page = 1;
    this.limitForm.patchValue({ limit: this.size });
    if (this.filterFields) { this.get_filtered_list(); }
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  // ─── Sorting ──────────────────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column, this.sort_type, sort_column, sort_type
    );

    this.sort_column = column;
    this.sort_type   = type;

    this.get_filtered_list();
  }

  // ─── Pagination ───────────────────────────────────────────────────────────

  setPage(num: number): void {
    if (num < 1 || num > this.total_pages || num === this.current_page) { return; }
    this.current_page = num;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  // ─── Enable / Disable with popup dialog ──────────────────────────────────

  disable(uid: any): void {
    this.popUpDialog.openStatusActionDialog(
      'Are you sure you want to disable this account?',
      'DISABLE',
      'CANCEL',
      () => this.executeStatusToggle(uid, 1),
      () => console.log('disable cancelled')
    );
  }

  enable(uid: any): void {
    this.popUpDialog.openStatusActionDialog(
      'Are you sure you want to enable this account?',
      'ENABLE',
      'CANCEL',
      () => this.executeStatusToggle(uid, 0),
      () => console.log('enable cancelled')
    );
  }

  directVerifyEmail(uid: any): void {
    this.popUpDialog.openStatusActionDialog(
      'Are you sure you want to verify this email? <br>This action cannot be undone.',
      'VERIFY',
      'CANCEL',
      () => this.toggleVerifyEmail(uid),
      () => console.log('verify email cancelled')
    );
  }

  private executeStatusToggle(uid: any, statusCode: number): void {
    this.togglingUid = uid;
    this.spinner.show().catch(console.error);

    this.apiService.updateAccountDetails(uid, { status: statusCode })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.toastr.success('Account status has been successfully updated!');
            this.get_filtered_list();
          } else {
            this.popUpDialog.openErrorDialog(
              'Failed to update account status. Please try again later.'
            );
          }
        },
        error: (err) => {
          this.popUpDialog.openErrorDialog('Something went wrong. Please try again later.');
        },
        complete: () => {
          this.togglingUid = null;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  private toggleVerifyEmail(uid: any): void {
    this.togglingUid = uid;
    this.spinner.show().catch(console.error);

    this.apiService.updateAccountsEmailStatus(uid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.toastr.success('Email Verified Successfully.');
            this.get_filtered_list();
          } else {
            this.popUpDialog.openErrorDialog(
              'Failed to update email verification status. Please try again later.'
            );
          }
        },
        error: (err) => {
          this.popUpDialog.openErrorDialog('Something went wrong. Please try again later.');
        },
        complete: () => {
          this.togglingUid = null;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  navigateToView(uid: any): void {
    this.router.navigate(['/accounts/view', uid]).catch(err => console.log('error navigate:', err));
  }

  // ─── Export ───────────────────────────────────────────────────────────────

  print(): void {
    const fileName = `${new Date().toISOString().slice(0, 10)}-accounts.xlsx`;
    this.spinner.show().catch(console.error);

       this.apiService.exportAccounts({ ...this.filterFields, search: this.search_keyword })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide().catch(console.error)),
      )
      .subscribe({
        next: (report: any) => {
          if (!(report instanceof Blob)) {
            this.toastr.error(
              'Something went wrong while exporting your document. Please check you connection and try again.',
            );
            return;
          }
          this.downloadBlob(report, fileName);
        },
        error: () => {
          this.toastr.error(
            'Something went wrong while exporting your document. Please check you connection and try again.',
          );
        },
      });
  }

  private downloadBlob(data: BlobPart, fileName: string): void {
    const blob = new Blob([data], { type: 'application/vnd.ms-excel' });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  // ─── Showing-entries helper ───────────────────────────────────────────────

  get showingText(): string {
    if (this.total === 0) { return 'No entries found'; }
    const displaying = Math.min(this.size, this.total - (this.current_page - 1) * this.size);
    return `Showing ${displaying} of ${this.total} entries`;
  }

  getIsEmailVerifiedLabel(item: boolean): string {
    return item ? 'True' : 'False';
  }

  getAdminButtonState(): { disabled: boolean; tooltip: string } {
    if (!this.authService.isAdmin()) {
      return {
        disabled: true,
        tooltip: 'You are not allowed to change details of accounts. Please contact an Admin if you need assistance',
      };
    }
    return { disabled: false, tooltip: '' };
  }
}
